import { addDays, differenceInMinutes, endOfMonth, endOfWeek, format, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { dayKey } from './agendaDay';

// Horários em que o doutor não atende, como faixas de fundo do calendário (Dia e Semana; o Mês e a "Agenda do mês" não
// mostram faixas de fundo) e como linhas da Lista do dia no celular. Calculados dos dias de atendimento do cadastro
// (service_hours), sem gravar nada: mudou o cadastro, mudam as faixas. Datas no fuso do navegador, como o calendário

export const OUT_OF_HOURS_TITLE = 'Fora do horário';

// Régua do Dia e da Semana: das 07:00 às 20:00. As faixas usam os mesmos limites
export const CALENDAR_START_HOUR = 7;
export const CALENDAR_END_HOUR = 20;

const toMinutes = (time) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

// Faixas de um dia da semana, em minutos, entre o começo e o fim do calendário e fora dos horários do doutor.
// Horários encostados ou sobrepostos não deixam faixa entre eles; dia sem horário fica inteiro fora
export function outOfHoursRanges(serviceHours, weekDay, dayStart, dayEnd) {
  const periods = serviceHours
    .filter((hour) => hour.week_day === weekDay)
    .map((hour) => [toMinutes(hour.start_time), toMinutes(hour.end_time)])
    .sort((a, b) => a[0] - b[0]);

  const ranges = [];
  let cursor = dayStart;
  periods.forEach(([start, end]) => {
    if (start > cursor) ranges.push([cursor, Math.min(start, dayEnd)]);
    cursor = Math.max(cursor, end);
  });
  if (cursor < dayEnd) ranges.push([cursor, dayEnd]);

  return ranges.filter(([start, end]) => end > start);
}

// Faixas de todos os doutores, do domingo da 1ª semana ao sábado da última semana do mês (a Semana pode cruzar o mês).
// Doutor sem a lista de horários (ex.: resposta sem service_hours) fica sem faixa; com a lista vazia, o dia todo fica fora,
// como na regra do backend, que recusa qualquer agendamento dele
export function outOfHoursEvents(doctors, monthDate, startHour, endHour) {
  const events = [];
  const last = endOfWeek(endOfMonth(monthDate));

  for (let day = startOfWeek(startOfMonth(monthDate)); day <= last; day = addDays(day, 1)) {
    const date = day;
    doctors.forEach((doctor) => {
      if (!Array.isArray(doctor.service_hours)) return;

      outOfHoursRanges(doctor.service_hours, date.getDay(), startHour * 60, endHour * 60).forEach(([start, end]) => {
        events.push({
          id: `out-of-hours-${doctor.id}-${dayKey(date)}-${start}`,
          title: OUT_OF_HOURS_TITLE,
          start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, start),
          end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, end),
          resourceId: doctor.id,
          outOfHours: true,
        });
      });
    });
  }

  return events;
}

// --------------------------------------------------------------------------------------------------------------
// Lista do dia (celular)

// Faixas de um dia, só dos doutores pedidos, em ordem de início e fim. Doutores com a mesma faixa (mesmo início e fim)
// ficam numa linha só, na ordem da lista pedida
export function outOfHoursRows(events, day, doctorIds) {
  const order = new Map(doctorIds.map((id, index) => [id, index]));
  const key = dayKey(day);
  const rows = new Map();

  events.forEach((event) => {
    if (!order.has(event.resourceId) || dayKey(event.start) !== key) return;
    const id = `${format(event.start, 'HHmm')}-${format(event.end, 'HHmm')}`;
    if (!rows.has(id)) rows.set(id, { key: `out-of-hours-${id}`, start: event.start, end: event.end, doctorIds: [], outOfHours: true });
    rows.get(id).doctorIds.push(event.resourceId);
  });

  return [...rows.values()]
    .map((row) => ({ ...row, doctorIds: [...row.doctorIds].sort((a, b) => order.get(a) - order.get(b)) }))
    .sort((a, b) => (a.start - b.start) || (a.end - b.end));
}

// Linha do tempo da Lista: os cartões (já em ordem) e as faixas, por início. No mesmo início, a faixa vem antes
export function withOutOfHours(items, rows) {
  const timeline = [];
  let next = 0;
  items.forEach((item) => {
    while (next < rows.length && rows[next].start <= item.start) timeline.push(rows[next++]);
    timeline.push(item);
  });
  return timeline.concat(rows.slice(next));
}

// Horário da faixa na Lista, que não tem a régua do calendário: as pontas do dia viram "Até 08:15" e "A partir das 19:00",
// e o dia inteiro, "Dia todo". No meio do dia: { start, end }
export function outOfHoursTime(row, startHour = CALENDAR_START_HOUR, endHour = CALENDAR_END_HOUR) {
  const fromDayStart = differenceInMinutes(row.start, startOfDay(row.start)) <= startHour * 60;
  const toDayEnd = differenceInMinutes(row.end, startOfDay(row.start)) >= endHour * 60;
  const start = format(row.start, 'HH:mm');
  const end = format(row.end, 'HH:mm');

  if (fromDayStart && toDayEnd) return { text: 'Dia todo' };
  if (fromDayStart) return { text: `Até ${end}` };
  if (toDayEnd) return { text: `A partir das ${start}` };
  return { start, end };
}

// "BRUNO", "BRUNO e PAULA", "ANA, BRUNO e PAULA"
export const joinNames = (names) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}` : names.join(''));
